import Footer from './Footer'
import { Header } from './Header'
import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser'; 
import { motion } from 'framer-motion';
import { href, useNavigate } from 'react-router-dom';
import { config } from '../config';
import NewsSection from './NewsSection';
import { Linkedin, ArrowRight, Mail, Phone, MapPin, Star, CheckCircle, TrendingUp, Shield, Zap } from 'lucide-react';

const stats = [
  { title: "Vision", description: "Creating transparent credit ecosystems" },
  { title: "Innovation", description: "Pioneering data-driven credit assessment" },
  { title: "Impact", description: "Reducing bad debt and strengthening businesses" }
];

const team = [
  { name: "Mannuri Vamshi Krishna ", title: "Founder", image: "vamshi Krishna sir.jpeg", href: "https://www.linkedin.com/in/mannuri-vamshi-krishna/" },
  { name: "Venkatesh Yanamaddi", title: "Co-Founder", image: "IMG-20250907-WA0014.png", href: "https://www.linkedin.com/in/venkatesh-yanamaddi/" },
  { name: "Raghavendra Koundinya", title: "Co-Founder", image: "Ragevendra sir.jpeg", href: "https://www.linkedin.com/in/chunduru-raghavendra-koundinya-859852139/" },
  { name: "Phanidra Sai Chunduru", title: "CMO", image: "Phani sir.jpeg", href: "https://www.linkedin.com/in/phanindra-sai-chunduru/" },
];

const supportteam = [
  { name: "Ajay Kumar", title: "Digital Marketing Specialist", image: "Men.png" },
  { name: "Vinay Kumar", title: "Data Analyst", image: "vinay.jpeg" },
  { name: "Dinesh", title: "Technical Support", image: "Dinesh.jpeg" },
  { name: "Srikanth Mankape", title: "ERP Specialist", image: "Srikanth.jpg" },
];

const testimonials = [
  {
    id: 1,
    text: "MedScore has completely changed the way I handle credit decisions. Earlier, I struggled with payment delays and untrustworthy customers, but now I have complete clarity on whom to trust. My cash flow has improved, and I feel more confident while giving credit. Every distributor and pharmacy should use MedScore to make smarter business decisions!",
    author: "Ravi",
    image: "men.png"
  },
  {
    id: 2,
    text: "Before MedScore, I was always worried about overdue payments. Now, I can easily check a retailer's credit history and make informed decisions. No more blind trust—just smart business!",
    author: "Chetan Penday",
    image: "men.png"
  },
  {
    id: 3,
    text: "MedScore is like my financial shield! It helps me identify risky customers, avoid bad debts, and improve my business growth. I highly recommend it to all pharma distributors and pharmacies!",
    author: "G.Sawmy",
    image: "men.png"
  },
  {
    id: 4,
    text: "Tracking payments and managing credit used to be a headache. With MedScore, I get real-time insights, better control over my receivables, and increased trust with my customers. It's a game-changer!",
    author: "Mahesh",
    image: "men.png"
  }
];

const HeroAnimatedLogos = () => {
  const [animationState, setAnimationState] = useState('initial');
  const timeoutRefs = useRef([]);
  
  useEffect(() => {
    const runAnimation = () => {
      const t1 = setTimeout(() => {
        setAnimationState('separated');
        
        const t2 = setTimeout(() => {
          setAnimationState('combining');
          
          const t3 = setTimeout(() => {
            setAnimationState('initial');
            
            const t4 = setTimeout(runAnimation, 3000);
            timeoutRefs.current.push(t4);
          }, 1800);
          timeoutRefs.current.push(t3);
        }, 3500);
        timeoutRefs.current.push(t2);
      }, 2000);
      timeoutRefs.current.push(t1);
    };
    
    const initialTimeout = setTimeout(runAnimation, 1000);
    timeoutRefs.current.push(initialTimeout);
    
    return () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="relative h-full w-full flex items-center justify-center"
    >
      <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-2xl blur-lg opacity-20 animate-pulse"></div>
      
      <div className="relative z-10 w-full h-96 rounded-2xl glass-dark border border-white/10 shadow-2xl overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        
        <div className="absolute inset-0 w-full h-full">
          {[...Array(15)].map((_, index) => (
            <div 
              key={index}
              className="absolute w-1 h-1 rounded-full bg-cyan-400 opacity-40"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 10}s infinite ease-in-out`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
        
        <div className="relative w-full max-w-md h-64">
          <div 
            className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 
              transition-all duration-1000 ease-in-out
              ${animationState === 'initial' ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
              ${animationState === 'combining' ? 'opacity-90 scale-95' : ''}
              z-20`}
          >
            <div className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-3xl font-bold px-8 py-4 rounded-lg shadow-xl">
              PCRA
            </div>
            <div className="absolute -inset-2 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-lg blur-md opacity-40 -z-10"></div>
          </div>
          
          <div 
            className={`absolute top-1/2 transform -translate-y-1/2 
              transition-all duration-1000 ease-in-out
              ${animationState === 'initial' ? 'left-1/2 -translate-x-1/2 opacity-0 scale-0' : 
                animationState === 'separated' ? 'left-1/4 -translate-x-1/2 opacity-100 scale-100' :
                'left-1/3 -translate-x-1/2 opacity-80 scale-95'}
              z-10`}
          >
            <div className="flex flex-col items-center group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full blur-md opacity-40"></div>
                <img src='M1WhiteLogo.png' className='w-28 h-28 relative z-10' alt="MedScore" />
              </div>
              <span className={`mt-3 text-white font-semibold text-lg transition-all duration-700 
                ${animationState === 'separated' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                MedScore
              </span>
            </div>
          </div>
          
          <div 
            className={`absolute top-1/2 transform -translate-y-1/2 
              transition-all duration-1000 ease-in-out
              ${animationState === 'initial' ? 'left-1/2 -translate-x-1/2 opacity-0 scale-0' : 
                animationState === 'separated' ? 'left-3/4 -translate-x-1/2 opacity-100 scale-100' :
                'left-2/3 -translate-x-1/2 opacity-80 scale-95'}
              z-10`}
          >
            <div className="flex flex-col items-center group">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full blur-md opacity-40"></div>
                <img src='safe_credits.png' className='w-28 h-28 mt-1 relative z-10' alt="SafeCredits" />
              </div>
              <span className={`mt-1 text-white font-semibold text-lg transition-all duration-700 
                ${animationState === 'separated' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
                SafeCredits
              </span>
            </div>
          </div>
          
          <svg className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${animationState === 'separated' ? 'opacity-70' : 'opacity-0'}`} viewBox="0 0 400 200">
            <line x1="100" y1="100" x2="200" y2="100" stroke="url(#line-gradient)" strokeWidth="2" strokeDasharray="5,5" />
            <line x1="300" y1="100" x2="200" y2="100" stroke="url(#line-gradient)" strokeWidth="2" strokeDasharray="5,5" />
            <defs>
              <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        
        <div 
          className={`absolute bottom-6 left-1/2 transform -translate-x-1/2 text-center transition-all duration-1000 ease-in-out mono text-xs
            ${animationState === 'initial' ? 'opacity-100' : 'opacity-50'}`}
        >
          <p className="text-slate-400">Integrated Financial Risk Solutions</p>
        </div>
      </div>
    </motion.div>
  );
};

export const Home = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([])
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    phone: '',
    businessname: '',
    message: ''
  })
  const [sending, setSending] = useState(false)
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  useEffect(() => {
    const getNewsData = async () => {
      try {
        const response = await fetch(`${config.API_HOST}/api/user/getNewsData`);
        if (response.ok) {
          const result = await response.json();
          setBlogPosts(result.data);
        }
      } catch (err) {
        console.error("Error fetching news data:", err);
      }
    };
    getNewsData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const templateParams = {
        to_name: "Medscore",
        from_name: formData.name,
        from_city: formData.city,
        phone: formData.phone,
        business_name: formData.businessname,
        message: formData.message
      };

      await emailjs.send(
        'service_tam6f72',
        'template_aadlafx',
        templateParams,
        'MB5Z4ns9hoVRCHL41'
      );

      alert('Message sent successfully!');
      setFormData({
        name: '',
        city: '',
        phone: '',
        businessname: '',
        message: ''
      });
    } catch (error) {
      alert('Failed to send message. Please try again.');
      console.error('EmailJS Error:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className='bg-slate-950 min-h-screen w-full text-white overflow-x-hidden'>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        
        * {
          font-family: 'Syne', sans-serif;
        }
        
        .mono {
          font-family: 'Space Mono', monospace;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        
        .glass-dark {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .grid-pattern {
          background-image: 
            linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.3;
          animation: pulse-glow 8s ease-in-out infinite;
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f0abfc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      <div className='fixed w-full top-0 left-0 z-50'>
        <Header/>
      </div>

      <div className="min-h-screen flex flex-col relative">
        {/* Animated Background Orbs */}
        <div className="orb w-96 h-96 bg-blue-500 top-20 -left-48 fixed" style={{animationDelay: '0s'}}></div>
        <div className="orb w-80 h-80 bg-purple-500 top-1/3 -right-40 fixed" style={{animationDelay: '2s'}}></div>
        <div className="orb w-72 h-72 bg-cyan-500 bottom-1/4 left-1/4 fixed" style={{animationDelay: '4s'}}></div>
        
        {/* Grid Pattern Overlay */}
        <div className="fixed inset-0 grid-pattern opacity-20 pointer-events-none"></div>
        
        <main className="flex-grow relative z-10">
          {/* Hero Section */}
          <section className="pt-36 pb-24">
            <div className="container mx-auto px-6 relative">
              <motion.div 
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="lg:flex items-center relative z-10 gap-16"
              >
                <div className="lg:w-1/2 space-y-8">
                  <div className="inline-flex items-center gap-2 glass-dark px-5 py-2.5 rounded-full text-sm font-semibold text-cyan-300">
                    <Zap size={16} className="text-cyan-400" />
                    <span className="mono">FINTECH INNOVATION</span>
                  </div>
                  
                  <h1 className="text-5xl lg:text-7xl font-black leading-tight">
                    <span className="block text-slate-200">Intelligent</span> 
                    <span className="text-gradient block">Credit Risk</span> 
                    <span className="block text-slate-200">Solutions</span>
                  </h1>
                  
                  <p className="text-xl text-slate-400 leading-relaxed max-w-2xl">
                    PCRA Technologies Pvt. Ltd. is a pioneering fintech startup revolutionizing trade credit risk management through patented and innovative platforms like <span className="text-blue-400 font-semibold">MedScore</span> and <span className="text-purple-400 font-semibold">SafeCredits</span>.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="group relative inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-600 to-emerald-600 px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all overflow-hidden"
                      onClick={() => navigate('/Medscore')}
                    >
                      <span className="relative z-10">Explore MedScore</span>
                      <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </motion.button>
                    
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center justify-center gap-3 glass-dark px-8 py-4 rounded-xl font-semibold text-lg text-slate-200 hover:bg-white/10 transition-all"
                      onClick={() => navigate('/SafeCredits')}
                    >
                      <span>Discover SafeCredits</span>
                      <ArrowRight size={20} />
                    </motion.button>
                  </div>
                </div>
                
                <div className="lg:w-1/2 mt-12 lg:mt-0">
                  <HeroAnimatedLogos />
                </div>
              </motion.div>
            </div>
          </section>

          {/* Solutions Overview */}
          <section className="py-24">
            <div className="container mx-auto px-6">
              <motion.div 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-cyan-300 mb-6">
                  <Shield size={16} />
                  <span className="mono">OUR SOLUTIONS</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold mb-4">
                  <span className="text-gradient">Cutting-edge</span> <span className="text-slate-200">Risk Assessment</span>
                </h2>
                <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                  Comprehensive credit risk assessment tools tailored for pharmaceutical distribution and business operations.
                </p>
              </motion.div>
              
              <div className="grid md:grid-cols-2 gap-12">
                {/* MedScore */}
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="group glass-dark rounded-3xl overflow-hidden hover:bg-white/5 transition-all border border-white/10"
                >
                  <div className="h-64 bg-gradient-to-br from-blue-900/50 to-indigo-900/50 flex items-center justify-center p-8 relative overflow-hidden border-b border-white/10">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-blue-500 blur-3xl"></div>
                      <div className="absolute bottom-10 left-10 w-24 h-24 rounded-full bg-cyan-500 blur-2xl"></div>
                    </div>
                    <img src="M1WhiteLogo.png" alt="MedScore Icon" className="h-32 relative z-10 group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-10">
                    <h3 className="text-3xl font-bold text-slate-100 mb-4">MedScore</h3>
                    <p className="text-slate-400 mb-8 text-lg leading-relaxed">
                      Advanced credit risk assessment for pharmaceutical distributors, combining industry-specific factors with AI-driven insights.
                    </p>
                    <ul className="space-y-4 mb-10">
                      {[
                        "Pharmaceutical-specific risk modeling",
                        "Real-time monitoring and alerts",
                        "Regulatory compliance integration"
                      ].map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mt-0.5">
                            <CheckCircle size={14} className="text-white" />
                          </div>
                          <span className="text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all w-full"
                      onClick={() => navigate('/Medscore')}
                    >
                      Learn More
                    </motion.button>
                  </div>
                </motion.div>
                
                {/* SafeCredits */}
                <motion.div 
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="group glass-dark rounded-3xl overflow-hidden hover:bg-white/5 transition-all border border-white/10"
                >
                  <div className="h-64 bg-gradient-to-br from-indigo-900/50 to-purple-900/50 flex items-center justify-center p-8 relative overflow-hidden border-b border-white/10">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-purple-500 blur-3xl"></div>
                      <div className="absolute bottom-10 right-10 w-24 h-24 rounded-full bg-pink-500 blur-2xl"></div>
                    </div>
                    <img src="Safe Credits Logo White.png" alt="SafeCredits Icon" className="h-32 relative z-10 group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-10">
                    <h3 className="text-3xl font-bold text-slate-100 mb-4">SafeCredits</h3>
                    <p className="text-slate-400 mb-8 text-lg leading-relaxed">
                      Comprehensive business credit assessment solution that analyzes financial health, payment history, and market factors.
                    </p>
                    <ul className="space-y-4 mb-10">
                      {[
                        "Multi-factor credit analysis",
                        "Customizable risk thresholds",
                        "Integration with financial systems"
                      ].map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mt-0.5">
                            <CheckCircle size={14} className="text-white" />
                          </div>
                          <span className="text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all w-full"
                      onClick={() => navigate('/SafeCredits')}
                    >
                      Learn More
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* How It Works */}
          <section id="works" className="py-24 relative">
            <div className="container mx-auto px-6 relative z-10">
              <motion.div 
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-cyan-300 mb-6">
                  <TrendingUp size={16} />
                  <span className="mono">PROCESS</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold mb-4">
                  <span className="text-slate-200">How It </span><span className="text-gradient">Works</span>
                </h2>
                <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                  Our streamlined process delivers accurate credit risk assessments in four simple steps.
                </p>
              </motion.div>
              
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  {
                    step: "1",
                    title: "Data Collection",
                    description: "Securely gather financial and operational data from multiple sources.",
                    gradient: "from-blue-500 to-cyan-600",
                    delay: 0.1
                  },
                  {
                    step: "2",
                    title: "Data Analysis",
                    description: "We review raw information, analyze, process, and clean data.",
                    gradient: "from-purple-500 to-indigo-600",
                    delay: 0.2
                  },
                  {
                    step: "3", 
                    title: "Risk Scoring",
                    description: "Generate comprehensive risk scores with detailed insights.",
                    gradient: "from-pink-500 to-purple-600",
                    delay: 0.3
                  },
                  {
                    step: "4",
                    title: "Actionable Reports",
                    description: "Receive detailed reports with recommended actions and monitoring.",
                    gradient: "from-emerald-500 to-teal-600",
                    delay: 0.4
                  }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: item.delay }}
                    className="relative group"
                  >
                    <div className="glass-dark rounded-2xl p-8 hover:bg-white/5 transition-all border border-white/10 h-full">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                        {item.step}
                      </div>
                      <h3 className="text-xl font-bold text-slate-100 mb-3 text-center">{item.title}</h3>
                      <p className="text-slate-400 text-center leading-relaxed">{item.description}</p>
                    </div>
                    {index < 3 && (
                      <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-white/20 to-transparent -z-10"></div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* About Section */}
          <section id="about" className="py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="mb-16 text-center">
                <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-cyan-300 mb-6">
                  <Shield size={16} />
                  <span className="mono">WHO WE ARE</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold mb-4">
                  <span className="text-slate-200">About </span><span className="text-gradient">PCRA Technologies</span>
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-emerald-500 mx-auto mb-12 rounded-full"></div>
              </div>
              
              <div className="mb-16 max-w-4xl mx-auto space-y-6">
                <p className="text-lg leading-relaxed text-slate-400">
                  PCRA Technologies Pvt. Ltd. is a pioneering fintech startup revolutionizing credit risk management through 
                  innovative platforms like <span className="text-blue-400 font-semibold">MedScore</span> and <span className="text-purple-400 font-semibold">SafeCredits</span>. We empower businesses to mitigate bad debts and strengthen 
                  financial systems using advanced data analytics.
                </p>
                
                <p className="text-lg leading-relaxed text-slate-400">
                  We are a forward-thinking company dedicated to innovation in credit risk management across healthcare, pharmaceutical, 
                  and broader business sectors. Our mission is to bring unprecedented transparency and efficiency to credit-based transactions, 
                  supporting financial trust and creditworthiness for both suppliers and retailers.
                </p>
              </div>
              
              <div className="mb-24">
                <h3 className="text-3xl font-bold text-slate-200 mb-12 text-center">Our Journey</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className="text-center p-8 glass-dark rounded-2xl border border-white/10 hover:bg-white/5 transition-all group"
                    >
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">{index + 1}</span>
                      </div>
                      <div className="text-2xl font-bold text-gradient mb-4">{stat.title}</div>
                      <div className="text-slate-400">{stat.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Advisers Section */}
              <section className="py-12 relative">
                <div className="text-center mb-16">
                  <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-cyan-300 mb-6">
                    <Star size={16} />
                    <span className="mono">OUR GUIDANCE</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-4">
                    <span className="text-slate-200">Advisers  </span>
                  </h2>
                  <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                    Strategic partners who drive our vision and growth through their invaluable expertise.
                  </p>
                </div>
                
                <div className="max-w-5xl mx-auto">
                  <div className="glass-dark rounded-3xl border border-white/10 overflow-hidden hover:bg-white/5 transition-all">
                    <div className="flex flex-col md:flex-row p-10 gap-8">
                      <div className="md:w-1/4 flex justify-center items-center">
                        <div className="w-40 h-40 rounded-2xl overflow-hidden border-4 border-cyan-500/30 shadow-xl">
                          <img 
                            src="Ram Krishna Garu.jpeg" 
                            alt="Ram Botta" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="md:w-3/4 space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-2xl font-bold text-slate-100 mb-2">Ram Botta</h3>
                            <div className="h-1 w-16 bg-gradient-to-r from-cyan-500 to-emerald-500 mb-4 rounded-full"></div>
                          </div>
                          <a 
                            href="https://www.linkedin.com/in/ramakrishna-botta/" 
                            className="glass-dark p-3 rounded-xl hover:bg-white/10 transition-all border border-white/10"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Linkedin size={20} className="text-cyan-400" />
                          </a>
                        </div>
                        
                        <p className="text-slate-400 leading-relaxed">
                          A dynamic HR and Strategy leader with 16 years of experience driving organizational growth and transformation. 
                          He brings deep expertise in strategic HR, leadership development, and culture building.
                        </p>
                        <p className="text-slate-400 leading-relaxed">
                          For over a decade, he has played a key role in building strong Supply Chain and Commercial teams across organizations. 
                          Well-versed in advanced practices like NLP and contemporary coaching, he is a passionate advisor known for his 
                          people-centric approach and impact in fast-paced startup ecosystems.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Leadership Team */}
              <div className="mt-24">
                <h3 className="text-3xl font-bold text-slate-200 mb-12 text-center">Leadership Team</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {team.map((member, index) => (
                    <div key={index} className="text-center group">
                      <div className="relative mb-6">
                        <div className="absolute -inset-1 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
                        <div className="relative w-32 h-32 mx-auto rounded-2xl overflow-hidden border-2 border-white/20">
                          <img 
                            src={member.image} 
                            alt={member.name} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <div className="font-bold text-lg mb-2 text-slate-200">{member.name}</div>
                      <div className="text-cyan-400 font-medium text-sm mb-3">{member.title}</div>
                      <a 
                        href={member.href} 
                        className="inline-flex glass-dark p-2 rounded-lg hover:bg-white/10 transition-all border border-white/10"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Linkedin size={16} className="text-cyan-400" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* Support Team */}
              {/* <div className="mt-24">
                <h3 className="text-3xl font-bold text-slate-200 mb-12 text-center">Support Team</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {supportteam.map((member, index) => (
                    <div key={index} className="text-center group">
                      <div className="relative mb-6">
                        <div className="absolute -inset-1 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
                        <div className="relative w-32 h-32 mx-auto rounded-2xl overflow-hidden border-2 border-white/20">
                          <img 
                            src={member.image} 
                            alt={member.name} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <div className="font-bold text-lg mb-2 text-slate-200">{member.name}</div>
                      <div className="text-purple-400 font-medium text-sm">{member.title}</div>
                    </div>
                  ))}
                </div>
              </div> */}
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-20">
                <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-cyan-300 mb-6">
                  <Star size={16} />
                  <span className="mono">TESTIMONIALS</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold mb-4">
                  <span className="text-slate-200">What Our </span><span className="text-gradient">Clients Say</span>
                </h2>
                <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                  Trusted by leading pharmaceutical distributors and businesses worldwide.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {testimonials.map(testimonial => (
                  <div 
                    key={testimonial.id} 
                    className="glass-dark p-10 rounded-2xl border border-white/10 hover:bg-white/5 transition-all group"
                  >
                    <div className="flex mb-6 gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={20} className="fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-lg mb-8 leading-relaxed text-slate-300">"{testimonial.text}"</p>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-cyan-500/30">
                        <img src={testimonial.image} alt={testimonial.author} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-slate-200">{testimonial.author}</h4>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* News Section */}
          <NewsSection/>

          {/* Contact Section */}
          <section id="contact" className="py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="glass-dark rounded-3xl border border-white/10 overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2 bg-gradient-to-br from-blue-900/50 to-purple-900/50 p-12 flex flex-col justify-center border-r border-white/10">
                    <h2 className="text-4xl font-bold mb-8 text-slate-100">Let's Connect</h2>
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                      Ready to transform your credit risk management approach? Our team is here to help you implement solutions tailored to your business needs.
                    </p>
                    
                    <div className="space-y-6">
                      <a href="tel:9347070310" className="flex items-start gap-4 group">
                        <div className="glass-dark p-3 rounded-xl border border-white/10 group-hover:bg-white/10 transition-all">
                          <Phone size={20} className="text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-400 mb-1 mono">PHONE</p>
                          <p className="text-lg text-slate-200">+91 7036222635</p>
                        </div>
                      </a>
                      
                      <a href="mailto:support@medscore.in" className="flex items-start gap-4 group">
                        <div className="glass-dark p-3 rounded-xl border border-white/10 group-hover:bg-white/10 transition-all">
                          <Mail size={20} className="text-purple-400" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-400 mb-1 mono">EMAIL</p>
                          <p className="text-lg text-slate-200">pcratechno@gmail.com</p>
                        </div>
                      </a>
                      
                      <div className="flex items-start gap-4">
                        <div className="glass-dark p-3 rounded-xl border border-white/10">
                          <MapPin size={20} className="text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-400 mb-1 mono">LOCATION</p>
                          <p className="text-lg text-slate-200">Hyderabad, India</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="md:w-1/2 p-12">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-slate-300 mb-2 font-medium text-sm mono">NAME</label>
                          <input 
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full p-3 glass-dark border border-white/10 rounded-xl text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all placeholder-slate-500" 
                            placeholder="Your name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-300 mb-2 font-medium text-sm mono">CITY</label>
                          <input 
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="w-full p-3 glass-dark border border-white/10 rounded-xl text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all placeholder-slate-500"
                            placeholder="Your city"
                            required 
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-slate-300 mb-2 font-medium text-sm mono">PHONE</label>
                          <input 
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full p-3 glass-dark border border-white/10 rounded-xl text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all placeholder-slate-500"
                            placeholder="+91 XXXXXXXXXX"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-slate-300 mb-2 font-medium text-sm mono">BUSINESS NAME</label>
                          <input 
                            type="text"
                            name="businessname"
                            value={formData.businessname}
                            onChange={handleChange}
                            className="w-full p-3 glass-dark border border-white/10 rounded-xl text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all placeholder-slate-500"
                            placeholder="Your business"
                            required 
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-slate-300 mb-2 font-medium text-sm mono">MESSAGE</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className="w-full p-3 glass-dark border border-white/10 rounded-xl h-32 text-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all placeholder-slate-500 resize-none"
                          placeholder="Tell us about your needs..."
                          required 
                        ></textarea>
                      </div>
                      
                      <button 
                        type="submit"
                        disabled={sending}
                        className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span>{sending ? 'Sending...' : 'Send Message'}</span>
                        {!sending && <ArrowRight size={20} />}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        
        <Footer/>
      </div>
    </div>
  )
}